import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useMemo } from 'react';

import { TimeZone, timezones } from '@controlrooms/constants';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

interface UseFilteredTimezonesProps {
  searchTerm?: string;
  plantTimezone?: string;
  computerTimezone?: string;
}

export const useFilteredTimezones = ({
  searchTerm = '',
  plantTimezone = 'UTC',
  computerTimezone,
}: UseFilteredTimezonesProps = {}) => {
  // Get computer timezone
  const detectedComputerTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }, []);

  const actualComputerTimezone = computerTimezone || detectedComputerTimezone;

  // Filter and sort timezones (DST-aware deduplication)
  const filteredTimezones = useMemo(() => {
    // Group timezones by their base IANA identifier
    const timezoneGroups = new Map<string, TimeZone[]>();

    timezones.forEach((tz) => {
      // Extract base IANA identifier (e.g., "America/Los_Angeles" from "America/Los_Angeles")
      const baseIanaId = tz.utc[0];
      if (!timezoneGroups.has(baseIanaId)) {
        timezoneGroups.set(baseIanaId, []);
      }
      const group = timezoneGroups.get(baseIanaId);
      if (group) {
        group.push(tz);
      }
    });

    // For each group, select only the currently active timezone variant
    const activeTimezones: TimeZone[] = [];

    timezoneGroups.forEach((group) => {
      if (group.length === 1) {
        // Only one variant, include it
        activeTimezones.push(group[0]);
      } else {
        // Multiple variants (DST/Standard), select the currently active one
        let activeTimezone = group[0]; // Default to first one

        // Check if we're currently in DST for this timezone
        let isCurrentlyDST = false;
        try {
          const now = dayjs();
          const january = dayjs().month(0).date(1); // January 1st (standard time)

          const januaryOffset = dayjs.tz(january, group[0].utc[0]).utcOffset();
          const currentOffset = dayjs.tz(now, group[0].utc[0]).utcOffset();

          // If current offset is different from January (standard time), we're in DST
          isCurrentlyDST = currentOffset !== januaryOffset;
        } catch (error) {
          // If timezone is invalid, fall back to checking the isdst field
          isCurrentlyDST = group.some((tz) => tz.isdst);
        }

        // Find the timezone variant that matches the current DST status
        for (const tz of group) {
          if (tz.isdst === isCurrentlyDST) {
            activeTimezone = tz;
            break;
          }
        }

        activeTimezones.push(activeTimezone);
      }
    });

    let filtered = activeTimezones;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (tz) =>
          tz.value.toLowerCase().includes(searchLower) ||
          tz.text.toLowerCase().includes(searchLower) ||
          tz.abbr.toLowerCase().includes(searchLower) ||
          tz.utc.some((utc) => utc.toLowerCase().includes(searchLower)),
      );
    }

    // Sort by offset (UTC+14:00 first, then UTC+13:00, etc., down to UTC-12:00)
    return filtered.sort((a, b) => b.offset - a.offset);
  }, [searchTerm]);

  // Get timezone data for plant and computer timezones (DST-aware)
  const plantTimezoneData = useMemo(() => {
    const matchingTimezones = timezones.filter((tz) => tz.utc.includes(plantTimezone));
    if (matchingTimezones.length <= 1) {
      return matchingTimezones[0];
    }

    // Check if we're currently in DST for this timezone
    let isCurrentlyDST = false;
    try {
      const now = dayjs();
      const january = dayjs().month(0).date(1); // January 1st (standard time)
      const currentOffset = dayjs.tz(now, plantTimezone).utcOffset();
      const januaryOffset = dayjs.tz(january, plantTimezone).utcOffset();

      // If current offset is different from January (standard time), we're in DST
      isCurrentlyDST = currentOffset !== januaryOffset;
    } catch (error) {
      // If timezone is invalid, fall back to checking the isdst field
      isCurrentlyDST = matchingTimezones.some((tz) => tz.isdst);
    }

    // Find the timezone variant that matches the current DST status
    for (const tz of matchingTimezones) {
      if (tz.isdst === isCurrentlyDST) {
        return tz;
      }
    }

    return matchingTimezones[0]; // Fallback to first one
  }, [plantTimezone]);

  const computerTimezoneData = useMemo(() => {
    const matchingTimezones = timezones.filter((tz) => tz.utc.includes(actualComputerTimezone));
    if (matchingTimezones.length <= 1) {
      return matchingTimezones[0];
    }

    // Check if we're currently in DST for this timezone
    let isCurrentlyDST = false;
    try {
      const now = dayjs();
      const january = dayjs().month(0).date(1); // January 1st (standard time)
      const currentOffset = dayjs.tz(now, actualComputerTimezone).utcOffset();
      const januaryOffset = dayjs.tz(january, actualComputerTimezone).utcOffset();

      // If current offset is different from January (standard time), we're in DST
      isCurrentlyDST = currentOffset !== januaryOffset;
    } catch (error) {
      // If timezone is invalid, fall back to checking the isdst field
      isCurrentlyDST = matchingTimezones.some((tz) => tz.isdst);
    }

    // Find the timezone variant that matches the current DST status
    for (const tz of matchingTimezones) {
      if (tz.isdst === isCurrentlyDST) {
        return tz;
      }
    }

    return matchingTimezones[0]; // Fallback to first one
  }, [actualComputerTimezone]);

  // Get timezone abbreviation for display (DST-aware)
  const getTimezoneAbbr = (timezone: string) => {
    const matchingTimezones = timezones.filter((tz) => tz.utc.includes(timezone));
    if (matchingTimezones.length <= 1) {
      return matchingTimezones[0]?.abbr || timezone;
    }

    // Check if we're currently in DST for this timezone
    let isCurrentlyDST = false;
    try {
      const now = dayjs();
      const january = dayjs().month(0).date(1); // January 1st (standard time)
      const currentOffset = dayjs.tz(now, timezone).utcOffset();
      const januaryOffset = dayjs.tz(january, timezone).utcOffset();

      // If current offset is different from January (standard time), we're in DST
      isCurrentlyDST = currentOffset !== januaryOffset;
    } catch (error) {
      // If timezone is invalid, fall back to checking the isdst field
      isCurrentlyDST = matchingTimezones.some((tz) => tz.isdst);
    }

    // Find the timezone variant that matches the current DST status
    for (const tz of matchingTimezones) {
      if (tz.isdst === isCurrentlyDST) {
        return tz.abbr;
      }
    }

    return matchingTimezones[0]?.abbr || timezone; // Fallback to first one
  };

  return {
    filteredTimezones,
    plantTimezoneData,
    computerTimezoneData,
    computerTimezone: actualComputerTimezone,
    getTimezoneAbbr,
  };
};
